import { Form } from "antd";
import _ from "lodash";
import React, {
  forwardRef,
  useCallback,
  useMemo,
  useRef,
} from "react";
import ToolsIntegrationsAPI from "restAPI/ToolsIntegrationAPI";
import { FieldTypes } from "utils/formUtils";

import GenericMappingForm from "../GenericMappingForm";

const PenTestToolMappingForm = forwardRef(
  ({ disableProjectFields = false, ...props }, ref) => {

    const [form] = Form.useForm();
    const IntRef = useRef({});

    const projectRef = useRef({
      workspace: [],
      asset: {},  
    });

    const fetchWorkspace = useCallback(
      (params) => {
        const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(
          props.toolName,
          { login_id: props.loginConfigId, ...params }
        );

        promise.then((response) => {
          projectRef.current.workspace = _.uniqBy(
            [...projectRef.current.workspace, ...response.projects], 
            'id'
          );
        });
        return { promise, cancel };
      },
      [props]
    );

    const fetchAsset = useCallback(
      (params) => {
        const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(
          props.toolName,
          { login_id: props.loginConfigId, ...params }
        );

        promise.then((response) => {
          projectRef.current.asset[params.project] =  _.uniqBy(
            [...(projectRef.current.asset[params.project] || []), ...response.projects],
            'id'
          );
        });

        return { promise, cancel };
      },
      [props]
    );

    const onWorkspaceChange = useCallback(
      (value, field) => {
        const { name } = field;
        const configurations = form.getFieldValue("configurations") || [];
        const updatedConfiguration = [...configurations];
        updatedConfiguration[name] = {
          ...updatedConfiguration[name],
          assetId: undefined,
        };
        form.setFieldsValue({ configurations: updatedConfiguration });
      },
      [form]
    );

    const getExtraFieldsData = useCallback((config) => {

      const { workspaceId, assetId = [] } = config;
      const workspaceName = projectRef.current.workspace.find(
        (w) => w.id === workspaceId
      )?.name;
      const data = projectRef.current.asset[workspaceId]?.reduce(
        (acc, cv) => {
          const { id, name } = cv;
          if (assetId.includes(id)) {
            acc.id.push(id);
            acc.name.push(name);
          }
          return acc;
        },
        { id: [], name: [] }
      );
      return {
        workspaceId,
        workspaceName,
        assetId: data.id.join(),
        assetName: data.name.join(),
      };
    }, []);

    const parseExtraFieldsDataFromResponse = useCallback((config) => {
      const { workspaceName, workspaceId, assetName, assetId } = config;
      return {
        workspaceName,
        workspaceId,
        ...(assetName && { assetName: assetName.split(",") }),
        ...(assetId && { assetId: assetId.split(",") }),
      };
    }, []);

    const getIsAssetFieldDisabled = useCallback((fieldKey, form) => {
      const fieldConfiguration = form.getFieldValue("configurations")[fieldKey];
      return !fieldConfiguration || !fieldConfiguration.workspaceId;
    }, []);

    const extraFields = useMemo(
      () => [
        {
          name: "workspaceId",
          colSpan: 10,
          type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
          label: "Workspace",
          required: true,
          disabled: disableProjectFields,
          props: {
            api: fetchWorkspace,
            onChange: onWorkspaceChange,
          },
        },
        {
          name: "assetId",
          colSpan: 12,
          type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
          label: "Assets",
          required: true,
          propsGenerator: (fieldKey) => ({
            disabled: getIsAssetFieldDisabled(fieldKey, form),
          }),
          props: {
            setExtraProps: (field, listField) => {
              const allConfigs = form.getFieldsValue().configurations;
              const workspace = allConfigs[listField.name]?.workspaceId;
              if (!IntRef.current[workspace]) {
                IntRef.current[workspace] = { project: workspace };
              }

              return {
                api: workspace && fetchAsset,
                apiParams: IntRef.current[workspace],
                resetOptionsProp: workspace,
                mode: "multiple",
                allowClear: true,
              };
            },
          },
        },
      ],
      [disableProjectFields, fetchWorkspace, onWorkspaceChange, getIsAssetFieldDisabled, form, fetchAsset]
    );

    return (
      <GenericMappingForm
        ref={ref}
        {...props}
        form={form}
        getExtraFieldsData={getExtraFieldsData}
        parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
        extraFields={extraFields}
      />
    );
  }
);

export default PenTestToolMappingForm;
